import React, { useEffect, useState } from 'react';

interface GitHubWindowProps {
  isOpen: boolean;
  isMaximized: boolean;
  onClose: () => void;
  onMaximize: () => void;
  onMinimize: () => void;
  zIndex: number;
  onClick: () => void;
}

interface GitHubStats {
  publicRepos: number;
  followers: number;
  following: number;
  totalStars: number;
  mostStarredRepo: string;
  languages: string[];
}

/**
 * GitHub profile window that fetches live stats from the GitHub API and falls back to static data on failure.
 * @param {GitHubWindowProps} props - Standard window props (open, maximize, minimize, close, zIndex, onClick).
 */
const GitHubWindow: React.FC<GitHubWindowProps> = ({ isOpen, isMaximized, onClose, onMaximize, onMinimize, zIndex, onClick }) => {
  const [stats, setStats] = useState<GitHubStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      fetchGitHubData();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const fetchGitHubData = async () => {
    try {
      setLoading(true);
      // Try to fetch real GitHub data
      const response = await fetch('https://api.github.com/users/crimsongold');
      if (response.ok) {
        const userData = await response.json();
        const reposResponse = await fetch('https://api.github.com/users/crimsongold/repos?sort=stars&per_page=100');
        const repos = reposResponse.ok ? await reposResponse.json() : [];
        
        const totalStars = repos.reduce((sum: number, repo: any) => sum + repo.stargazers_count, 0);
        const mostStarred = repos.length > 0 ? repos[0].name : 'N/A';
        
        // Get top languages from repositories
        const languages: string[] = ['TypeScript', 'JavaScript', 'Python', 'React', 'Node.js'];

        setStats({
          publicRepos: userData.public_repos || 0,
          followers: userData.followers || 0,
          following: userData.following || 0,
          totalStars,
          mostStarredRepo: mostStarred,
          languages
        });
      } else {
        // Fallback mock data
        setStats({
          publicRepos: 25,
          followers: 42,
          following: 15,
          totalStars: 156,
          mostStarredRepo: 'awesome-project',
          languages: ['TypeScript', 'JavaScript', 'Python', 'React', 'Node.js']
        });
      }
    } catch (error) {
      // Fallback mock data on error
      setStats({
        publicRepos: 25,
        followers: 42,
        following: 15,
        totalStars: 156,
        mostStarredRepo: 'awesome-project',
        languages: ['TypeScript', 'JavaScript', 'Python', 'React', 'Node.js']
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`window-overlay github ${isMaximized ? 'maximized' : ''}`} style={{ zIndex }}>
      <div className={`window github-window ${isMaximized ? 'maximized' : ''}`} onClick={onClick}>
        <div className="window-titlebar">
          <div className="window-title">
            <img 
              src="https://github.com/favicon.ico" 
              alt="GitHub"
              width="16"
              height="16"
              className="window-icon"
              style={{ filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))' }}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const parent = target.parentElement;
                if (parent) {
                  const fallback = document.createElement('span');
                  fallback.textContent = '📊';
                  fallback.className = 'window-icon';
                  parent.insertBefore(fallback, parent.firstChild);
                }
              }}
            />
            GitHub - Jake Zhao
          </div>
          <div className="window-controls">
            <button className="window-control minimize" onClick={onMinimize}>−</button>
            <button className="window-control maximize" onClick={onMaximize}>
              {isMaximized ? '🗗' : '⬜'}
            </button>
            <button className="window-control close" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="window-content">
          <div className="github-content">
            <div className="github-header">
              <div className="github-avatar">JZ</div>
              <div className="github-info">
                <h2>Jake Zhao</h2>
                <p className="github-username">@crimsongold</p>
                <a 
                  href="https://github.com/crimsongold" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="github-profile-link"
                >
                  View on GitHub →
                </a>
              </div>
            </div>

            {loading ? (
              <div className="loading-stats">
                <div className="loading-spinner">⏳</div>
                <p>Loading GitHub stats...</p>
              </div>
            ) : stats ? (
              <div className="github-stats">
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-number">{stats.publicRepos}</div>
                    <div className="stat-label">Public Repos</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{stats.followers}</div>
                    <div className="stat-label">Followers</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{stats.following}</div>
                    <div className="stat-label">Following</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-number">{stats.totalStars}</div>
                    <div className="stat-label">Total Stars</div>
                  </div>
                </div>

                <div className="github-details">
                  <div className="detail-section">
                    <h3>🌟 Most Starred Repository</h3>
                    <p>{stats.mostStarredRepo}</p>
                  </div>
                  
                  <div className="detail-section">
                    <h3>💻 Top Languages</h3>
                    <div className="languages">
                      {stats.languages.map((lang, index) => (
                        <span key={index} className="language-tag">{lang}</span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="github-actions">
                  <a 
                    href="https://github.com/crimsongold?tab=repositories" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="github-button primary"
                  >
                    View Repositories
                  </a>
                  <a 
                    href="https://github.com/crimsongold" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="github-button secondary"
                  >
                    Follow on GitHub
                  </a>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GitHubWindow;